import React, { ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import Spinner from 'component/Layout/Loader/Spinner';
import Input from 'component/Search/Input';

import classNames from 'classnames';

import { useOutsideClick } from 'hooks/useOutsideClick';
import { QaIdentifier, useQa } from 'hooks/useQa';

import { ReactComponent as IconChevronRounded } from 'media/svg/chevronRounded.svg';

import { lazyRetry } from 'utility/Lazy/Retry';

import { DurationType, DurationValue, ValidationType } from 'types/Search/form';

import { CustomSearchInputClassList } from '../Input/types';

import { buildSelectionFromValue } from './__helper/mapping';
import { defaultConfig } from './config';
import { ActiveDurationLayer, Config } from './types';

import styles from './Duration.module.scss';

const LazyLayerContent = React.lazy(() =>
    lazyRetry(() => import('./__partials/LayerContent'))
);

interface DurationProps {
    customClassList?: CustomSearchInputClassList;
    config?: Config;
    radioName?: string;
    value: DurationValue;
    onValueChange: (value: DurationValue) => void;
    errorComponent?: ReactNode;
    isLayerOpen?: ActiveDurationLayer;
    onBlur?: () => void;
    onOpen: () => void;
    onClose: () => void;
    highlightComponent?: ReactNode;
    label: string;
    placeholder: string;
    onValidate?: (type: ValidationType, isEmpty: boolean) => void;
}

const Duration = ({
    customClassList,
    config = defaultConfig,
    value,
    onValueChange,
    errorComponent,
    isLayerOpen = ActiveDurationLayer.None,
    onBlur,
    onOpen,
    onClose,
    radioName = 'duration',
    highlightComponent,
    label,
    placeholder,
    onValidate,
}: DurationProps) => {
    const { t } = useTranslation();

    const layerRef = useOutsideClick<HTMLDivElement>(() => onClose?.());
    const qaAttrLayer = useQa(QaIdentifier.Search_Form_Duration_Layer);

    const isEmptySelection = value.type === DurationType.Empty;

    return (
        <>
            <Input
                labelText={label}
                placeholder={placeholder}
                value={buildSelectionFromValue(value, t)}
                callbacks={{
                    onClick: () => {
                        if (isLayerOpen === ActiveDurationLayer.None) {
                            onOpen?.();
                        } else {
                            onValidate?.(
                                ValidationType.Duration,
                                isEmptySelection
                            );
                            onClose?.();
                        }
                    },
                    onBlur: () => {
                        onValidate?.(ValidationType.Duration, isEmptySelection);
                        onBlur?.();
                    },
                }}
                qaId={QaIdentifier.Search_Form_Duration_Value}
                isReadOnly
                customClassList={{
                    InputWrapper: classNames(
                        styles.InputWrapper,
                        customClassList?.InputWrapper
                    ),
                    Label: classNames(styles.Label, customClassList?.Label),
                    Input: classNames(
                        styles.Input,
                        {
                            [styles.Highlight]: !!highlightComponent,
                        },
                        styles.Button,
                        customClassList?.Input
                    ),
                    InputIconWrapper: customClassList?.InputIconWrapper,
                }}
            >
                <button
                    onClick={() => {
                        if (isLayerOpen === ActiveDurationLayer.None) {
                            onOpen?.();
                        } else {
                            onClose?.();
                            onValidate?.(
                                ValidationType.Duration,
                                isEmptySelection
                            );
                        }
                    }}
                >
                    <IconChevronRounded />
                </button>
            </Input>

            {isLayerOpen === ActiveDurationLayer.DurationLayer && (
                <div
                    className={classNames(
                        styles.DurationLayer,
                        customClassList?.Layer
                    )}
                    ref={layerRef}
                    {...qaAttrLayer}
                >
                    <Suspense
                        fallback={
                            <div className={styles.SpinnerWrapper}>
                                <Spinner
                                    classListObject={{
                                        Spinner: styles.Spinner,
                                    }}
                                />
                            </div>
                        }
                    >
                        <LazyLayerContent
                            config={config}
                            value={value}
                            radioName={radioName}
                            errorComponent={errorComponent}
                            highlightComponent={highlightComponent}
                            onClose={() => onClose?.()}
                            onValueChange={(newValue) => {
                                onValueChange(newValue);
                            }}
                        />
                    </Suspense>
                </div>
            )}
        </>
    );
};
export default Duration;
